import { useState } from "react";
import Icon from "../Icon";
import { ModalFt } from "../Atoms";
import DatePicker from "./DatePicker";
import Campo from "./Campo";

const TIPOS = [
  { v:"festivo",  label:"Festivo",  hint:"No cuenta como día laborable" },
  { v:"evento",   label:"Evento",   hint:"Reunión, hito, recordatorio" },
  { v:"tarea",    label:"Tarea",    hint:"Algo que hacer ese día" },
  { v:"ausencia", label:"Ausencia", hint:"Vacaciones, permiso, baja" },
];

// Body del modal central de App.jsx. Recibe los calendarios donde el usuario
// puede escribir; el tipo "festivo" solo tiene sentido en un calendario laboral,
// pero no se bloquea: basta con avisar de su efecto.
export default function EventoCalendarioForm({
  evento = null, calendarios = [], defaultDate = "", defaultCalendario = "",
  onSave, onDelete, onClose,
}) {
  const isNew = !evento?.id;
  const editables = calendarios.filter(c => c.can_edit && !c.archived);

  const [f, setF] = useState({
    calendario_id: evento?.calendario_id || defaultCalendario || editables[0]?.id || "",
    titulo:      evento?.titulo      || "",
    // fecha_base, no fecha: en un evento anual la fecha visible es la ya expandida
    fecha:       evento?.fecha_base  || evento?.fecha || defaultDate || "",
    fecha_fin:   evento?.fecha_fin   || "",
    all_day:     evento ? !!evento.all_day : true,
    hora_inicio: evento?.hora_inicio || "09:00",
    hora_fin:    evento?.hora_fin    || "10:00",
    tipo:        evento?.tipo        || "evento",
    descripcion: evento?.descripcion || "",
    anual:       !!evento?.anual,
    rango:       !!evento?.fecha_fin,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const cal = editables.find(c => String(c.id) === String(f.calendario_id));

  const errHora = !f.all_day && f.hora_fin && f.hora_inicio && f.hora_fin <= f.hora_inicio;
  const errFin  = f.rango && f.fecha_fin && f.fecha_fin < f.fecha;
  const valid   = !!f.titulo.trim() && !!f.fecha && !!f.calendario_id && !errHora && !errFin;

  const lbS  = {fontSize:10,color:"var(--text3)",fontWeight:700,display:"block",marginBottom:5,textTransform:"uppercase",letterSpacing:".06em"};
  const inS  = {width:"100%",padding:"9px 11px",borderRadius:8,border:"1px solid var(--border)",fontSize:13,fontFamily:"var(--font)",boxSizing:"border-box",background:"var(--surface2)",color:"var(--text)"};
  const secS = {marginBottom:14};
  const chkS = {display:"flex",alignItems:"center",gap:7,cursor:"pointer",fontSize:12,color:"var(--text2)"};

  const submit = async () => {
    if (!valid || saving) return;
    setSaving(true);
    try {
      await onSave({
        calendario_id: f.calendario_id,
        titulo:      f.titulo.trim(),
        fecha:       f.fecha,
        fecha_fin:   f.rango && f.fecha_fin ? f.fecha_fin : null,
        all_day:     f.all_day,
        hora_inicio: f.all_day ? null : f.hora_inicio,
        hora_fin:    f.all_day ? null : (f.hora_fin || null),
        tipo:        f.tipo,
        descripcion: f.descripcion.trim() || null,
        anual:       f.anual,
      });
    } finally { setSaving(false); }
  };

  if (!editables.length) return (
    <div>
      <h3 style={{margin:"0 0 8px",fontWeight:700,fontSize:16}}>Nuevo evento</h3>
      <p style={{fontSize:12.5,color:"var(--text3)",lineHeight:1.5,margin:"0 0 16px"}}>
        No tienes ningún calendario donde escribir. Crea uno propio desde
        &laquo;Gestionar calendarios&raquo; en la barra lateral.
      </p>
      <ModalFt onClose={onClose} label="Entendido" onSave={onClose}/>
    </div>
  );

  return (
    <div>
      <h3 style={{margin:"0 0 4px",fontWeight:700,fontSize:16,display:"flex",alignItems:"center",gap:8}}>
        <Icon name={isNew?"plus-circle":"pencil-square"} size={15} color="var(--accent)"/>
        {isNew ? "Nuevo evento" : "Editar evento"}
      </h3>
      <p style={{margin:"0 0 16px",fontSize:11.5,color:"var(--text3)",lineHeight:1.5}}>
        Puede ocupar el día entero o solo una franja horaria.
      </p>

      <div style={secS}>
        <Campo><label style={lbS}>Calendario</label>
        <select value={f.calendario_id} onChange={e=>set("calendario_id", e.target.value)} style={inS}>
          {editables.map(c => (
            <option key={c.id} value={c.id}>{c.empresa ? `${c.name} (empresa)` : c.name}</option>
          ))}
        </select></Campo>
      </div>

      <div style={secS}>
        <Campo><label style={lbS}>Título</label>
        <input value={f.titulo} onChange={e=>set("titulo", e.target.value)} style={inS}
          placeholder="Día de la Constitución, Reunión de equipo…" autoFocus/></Campo>
      </div>

      <div style={{...secS,display:"flex",gap:10}}>
        <div style={{flex:1}}>
          <Campo><label style={lbS}>{f.rango ? "Desde" : "Fecha"}</label>
          <DatePicker value={f.fecha} onChange={v=>set("fecha", v)}/></Campo>
        </div>
        {f.rango && (
          <div style={{flex:1}}>
            <Campo><label style={lbS}>Hasta</label>
            <DatePicker value={f.fecha_fin} onChange={v=>set("fecha_fin", v)} refDate={f.fecha}/></Campo>
          </div>
        )}
      </div>

      <div style={{...secS,display:"flex",gap:16,flexWrap:"wrap"}}>
        <label style={chkS}>
          <input type="checkbox" checked={f.rango} onChange={e=>set("rango", e.target.checked)}
            style={{accentColor:"var(--accent)",width:14,height:14}}/>
          Varios días
        </label>
        <label style={chkS}>
          <input type="checkbox" checked={f.all_day} onChange={e=>set("all_day", e.target.checked)}
            style={{accentColor:"var(--accent)",width:14,height:14}}/>
          Todo el día
        </label>
        <label style={chkS} title="Aparecerá cada año en el mismo día y mes, sin volver a crearlo">
          <input type="checkbox" checked={f.anual} onChange={e=>set("anual", e.target.checked)}
            style={{accentColor:"var(--accent)",width:14,height:14}}/>
          <Icon name="arrow-repeat" size={12} color="var(--text3)"/> Se repite cada año
        </label>
      </div>

      {!f.all_day && (
        <div style={{...secS,display:"flex",gap:10}}>
          <div style={{flex:1}}>
            <Campo><label style={lbS}>Hora inicio</label>
            <input type="time" value={f.hora_inicio} onChange={e=>set("hora_inicio", e.target.value)} style={inS}/></Campo>
          </div>
          <div style={{flex:1}}>
            <Campo><label style={lbS}>Hora fin</label>
            <input type="time" value={f.hora_fin} onChange={e=>set("hora_fin", e.target.value)} style={inS}/></Campo>
          </div>
        </div>
      )}

      <div style={secS}>
        <Campo><label style={lbS}>Tipo</label>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {TIPOS.map(t => (
            <button key={t.v} className="btn" title={t.hint} onClick={()=>set("tipo", t.v)}
              style={{padding:"6px 12px",borderRadius:8,cursor:"pointer",fontSize:12,fontFamily:"var(--font)",
                border:`1px solid ${f.tipo===t.v?"var(--accent)":"var(--border)"}`,
                background:f.tipo===t.v?"var(--accent-bg)":"var(--surface2)",
                color:f.tipo===t.v?"var(--accent)":"var(--text2)",
                fontWeight:f.tipo===t.v?700:500}}>
              {t.label}
            </button>
          ))}
        </div></Campo>
        {f.tipo==="festivo" && (
          <div style={{fontSize:11,color:"var(--text3)",marginTop:6,lineHeight:1.4}}>
            Los festivos se descuentan del recuento de días laborables
            {cal && !cal.empresa ? " solo para ti, porque este calendario es personal." : "."}
          </div>
        )}
      </div>

      <div style={secS}>
        <Campo><label style={lbS}>Descripción (opcional)</label>
        <textarea value={f.descripcion} onChange={e=>set("descripcion", e.target.value)}
          style={{...inS,minHeight:60,resize:"vertical"}}/></Campo>
      </div>

      {(errHora || errFin) && (
        <div style={{fontSize:11.5,color:"var(--red-text)",marginBottom:12}}>
          {errHora ? "La hora de fin debe ser posterior a la de inicio."
                   : "La fecha de fin no puede ser anterior al inicio."}
        </div>
      )}

      <div style={{display:"flex",alignItems:"center",gap:8,marginTop:6}}>
        {!isNew && onDelete && (
          <button className="btn" onClick={onDelete} disabled={saving}
            style={{padding:"8px 14px",borderRadius:9,border:"1px solid var(--border)",cursor:"pointer",
              fontSize:12.5,background:"var(--surface2)",color:"var(--red-text)",fontFamily:"var(--font)",
              display:"flex",alignItems:"center",gap:6}}>
            <Icon name="trash3" size={12} color="var(--red-text)"/> Eliminar
          </button>
        )}
        <div style={{flex:1}}/>
        <ModalFt onClose={onClose} label={saving ? "Guardando…" : (isNew ? "Crear evento" : "Guardar cambios")}
          onSave={submit} disabled={!valid || saving}/>
      </div>
    </div>
  );
}
